import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Minus, Navigation, Plus, RotateCcw } from 'lucide-react';

// Shop number -> SVG shape id. DKSN Business Solutions is Shop 23B.
export const DKSN_SHOP_ID: string | null = 'ddd';

const DKSN_ADDRESS = 'Shop 23B, Mall of Mthatha, 33 Errol Spring Ave, Mthatha, 5099';
const DKSN_DIRECTIONS_URL = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
  DKSN_ADDRESS
)}`;

// SVG shape id -> tenant name, from the mall's official store directory.
const STORE_NAMES: Record<string, string> = {
  qa: '@Home',
  ag: 'ABSA',
  oooooo: 'Ackermans',
  _x37_4: 'American Swiss',
  xxxxx: 'Archive',
  pppp: 'Assupol',
  ty: 'ATMs',
  ooo: 'Baby Room',
  _x31_7: 'Bathu',
  c: 'Bogart Man',
  rrrr: 'Boss Tailor',
  ss: 'Brazilia',
  _x36_666: 'Burger King',
  zzzz: 'Capitec',
  yy: 'Cash Crusaders',
  rr: 'Cell and Mac Fix',
  anchor3: 'Checkers',
  aj: 'Checkers Liquor',
  _x31_: 'Clicks',
  _x33_8: 'Cyber Solutions',
  qt: 'Debonairs',
  zz: 'Destination',
  anchor4: 'Dischem',
  ddddd: 'Domain',
  aaaaa: 'Drip',
  anchor5: 'Edgars',
  ff: 'Essence of the Middle East',
  uuuuuu: 'Exact',
  _x31_11: 'Execuspecs',
  ggggg: 'Fabiani',
  u: 'Fashion',
  qqqqqq: 'Fish & Chips Company',
  q: 'FNB',
  SH17C: 'Foschini',
  _x35_55: 'G Star Raw',
  _x31_9: 'Gadget Geeks',
  ti: 'Guess',
  lll: 'Headways',
  _x39_: 'Heavens Beauty Spa',
  t: 'Hi-Fi Corp',
  _x32_8: 'Home Choice',
  _x34_444: 'Home Electronics',
  al: 'House & Home',
  _x32_222: 'Hungry Lion',
  _x32_2: 'Icebolethu',
  eeeeee: 'Identity',
  _x33_0: 'Incredible Connection',
  yyyy: 'iSecure',
  _x33_6: 'Jam Clothing',
  _x34_8: 'Jet',
  ttttt: 'John Craig',
  bb: "John Dory's",
  qj: 'Karibu',
  qe: 'KFC',
  m: 'King Pie',
  hh: 'Le Coq Sportif',
  tttttt: 'Legit',
  _x37_2: 'Markhams',
  ddd: 'Medical Society',
  qo: 'Miladys',
  cccc: 'Mr Price',
  ssss: 'Mr Price Cellular',
  _x33_3: 'Mr Price Home',
  mmm: 'Mr Price Sport',
  hhhh: 'MTN',
  b: 'Mugg & Bean',
  z: 'Nandos',
  _x31_02: 'Nedbank',
  h: 'News Cafe',
  k: 'News Cafe',
  ad: 'Old Mutual',
  _x32_5: 'Panda',
  jjjj: 'PEP',
  _x34_: 'PEP Cell',
  dddddd: 'PEP Home',
  anchor1: 'Pick n Pay',
  _x37_77: 'Pick n Pay Clothing',
  _x31_8: 'Pick n Pay Liquor',
  jjj: 'Post Office',
  qqq: 'Pringle',
  vvvvv: 'Queenspark',
  _x32_3: 'Rage',
  _x34_6: 'Refinery',
  jjjjj: 'Relay',
  _x32_7: 'RFO',
  nn: 'Sama Fitness',
  _x36_: 'Shoprite',
  qg: 'Side Step',
  _x32_9: 'Silulo Ulutho Technologies',
  _x33_1: 'Skipper Bar',
  wwww: 'Sleepmasters',
  _x37_6: 'Sneaker Factory',
  cc: 'Soviet',
  _x31_6: 'SPCC',
  _x31_4: 'Spec Savers',
  uuu: 'Spitz',
  _x37_0: 'Sportscene',
  'spur-37d': 'Spur',
  qu: 'Standard Bank',
  uuuuu: 'Sterns',
  iiii: 'Studio 88',
  _x32_4: 'Tayano Couture',
  aaaaaa: 'Tekkie Town',
  bbb: 'Telkom',
  lllll: 'The Body Shop',
  tp: 'The Crazy Store',
  ooooo: 'The Fix',
  ttt: 'Timberland',
  qd: 'Totalsports',
  ccc: 'Traditional Clothing',
  _x34_4: 'Truworths',
  _x32_6: 'UFO',
  ii: 'United Cell & Sound',
  eee: 'Vacant',
  ggg: 'Vacant',
  _x32_: 'Vacant',
  _x33_5: 'Vacant',
  ww: 'Vacant',
  pp: 'Vacant',
  kk: 'Vacant',
  _x38_888: 'Vacant',
  ffff: 'Vacant',
  bbbb: 'Vacant',
  _x33_33: 'Vacant',
  nnnnn: 'Van Schaik',
  _x34_0: 'Volpes',
  _x32_0: 'Wimpy',
  anchor2: 'Woolworths',
};

interface MallMapProps {
  /** Temporary: shows shop ids on hover/click so you can find DKSN's shape id. */
  identifyMode?: boolean;
  /** Fired with the tenant name whenever a store is hovered, or null on hover-out. */
  onHoverStore?: (name: string | null) => void;
}

const BRAND_BLUE = '#5B8DEF';
const MIN_SCALE = 1;
const MAX_SCALE = 4;

type Transform = { scale: number; x: number; y: number };

// Hand-plotted routes from each of the mall's marked entrance/access icons to
// DKSN's shop, in the artwork's own coordinate space (same space the store
// shapes use, i.e. before the g.st40 scale(1.25) and before pan/zoom). Points
// were traced by eye off a rendered snapshot of the SVG against the visible
// gray walkway, since the artwork has no routable path data of its own.
const DKSN_TARGET: [number, number] = [666.4, 330.9];

interface EntranceRoute {
  id: string;
  label: string;
  name: string;
  waypoints: [number, number][];
}

const ENTRANCE_ROUTES: EntranceRoute[] = [
  {
    id: 'main',
    label: '1',
    name: 'Errol Spring Ave entrance',
    waypoints: [
      [734.6, 642.7],
      [733.4, 582.9],
      [726.3, 533.6],
      [744.2, 484.2],
      [708.4, 421.5],
      [677.1, 376.6],
      DKSN_TARGET,
    ],
  },
  {
    id: 'taxi',
    label: '2',
    name: 'Taxi holding area access',
    waypoints: [
      [690.6, 285.1],
      [681.5, 309.3],
      DKSN_TARGET,
    ],
  },
  {
    id: 'parking',
    label: '3',
    name: 'Open air parking',
    waypoints: [
      [320.1, 378.4],
      [385.6, 412.5],
      [430.5, 385.6],
      [502.2, 358.7],
      [555.9, 345.2],
      [609.7, 336.3],
      DKSN_TARGET,
    ],
  },
];

// Renders a list of waypoints as a smooth path (quadratic curves through
// each midpoint) rather than a jagged polyline, ending exactly on the shop.
const toSmoothPath = (points: [number, number][]): string => {
  if (points.length === 0) return '';
  if (points.length === 1) return `M ${points[0][0]} ${points[0][1]}`;

  let d = `M ${points[0][0]} ${points[0][1]}`;
  for (let i = 1; i < points.length - 1; i++) {
    const [x0, y0] = points[i];
    const [x1, y1] = points[i + 1];
    d += ` Q ${x0} ${y0} ${(x0 + x1) / 2} ${(y0 + y1) / 2}`;
  }
  const [lastX, lastY] = points[points.length - 1];
  d += ` L ${lastX} ${lastY}`;
  return d;
};

export const MallMap: React.FC<MallMapProps> = ({ identifyMode = false, onHoverStore }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const targetElRef = useRef<SVGGraphicsElement | null>(null);
  const routesGroupRef = useRef<SVGGElement | null>(null);
  const [svgMarkup, setSvgMarkup] = useState<string | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [pin, setPin] = useState<{ x: number; y: number } | null>(null);
  const [transform, setTransform] = useState<Transform>({ scale: 1, x: 0, y: 0 });
  const [activeRouteId, setActiveRouteId] = useState<string | null>(null);

  // --- Pan/zoom drag state (kept in refs so pointer handlers don't need to
  // re-subscribe on every render) ---
  const pointersRef = useRef<Map<number, { x: number; y: number }>>(new Map());
  const panStartRef = useRef<{ x: number; y: number } | null>(null);
  const pinchStartRef = useRef<{ dist: number; scale: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch('/svg/mall-of-mthatha.svg')
      .then((res) => res.text())
      .then((raw) => {
        if (cancelled) return;
        // Recolor the brand-yellow road/accent (#ffc809) to the site's azure blue.
        const recolored = raw.replace(/#ffc809/gi, BRAND_BLUE);
        setSvgMarkup(recolored);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Recomputes the pin's pixel position from the DKSN shape's actual rendered
  // bounding box, relative to the container. This is immune to viewBox
  // cropping, preserveAspectRatio letterboxing, container aspect ratio, and
  // any pan/zoom transform applied to the stage — it just reads real screen
  // coordinates after paint.
  const updatePinPosition = useCallback(() => {
    const container = containerRef.current;
    const target = targetElRef.current;
    if (!container || !target) return;

    const containerRect = container.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();

    setPin({
      x: targetRect.left + targetRect.width / 2 - containerRect.left,
      y: targetRect.top + targetRect.height / 2 - containerRect.top,
    });
  }, []);

  const clampTransform = useCallback((next: Transform): Transform => {
    const container = containerRef.current;
    const scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, next.scale));
    if (!container) return { ...next, scale };

    const { width, height } = container.getBoundingClientRect();
    const scaledWidth = width * scale;
    const scaledHeight = height * scale;

    const minX = Math.min(0, width - scaledWidth);
    const minY = Math.min(0, height - scaledHeight);

    return {
      scale,
      x: Math.min(0, Math.max(minX, next.x)),
      y: Math.min(0, Math.max(minY, next.y)),
    };
  }, []);

  const zoomAt = useCallback(
    (point: { x: number; y: number }, targetScale: number) => {
      setTransform((prev) => {
        const scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, targetScale));
        const contentX = (point.x - prev.x) / prev.scale;
        const contentY = (point.y - prev.y) / prev.scale;
        return clampTransform({
          scale,
          x: point.x - contentX * scale,
          y: point.y - contentY * scale,
        });
      });
    },
    [clampTransform]
  );

  // Keep the pin glued to the shop whenever pan/zoom changes. The stage
  // animates its CSS transform over 150ms when not dragging (zoom
  // buttons/wheel), so a single rAF right after the state change measures a
  // stale, mid-transition position. Track the animation across its full
  // duration instead of just the first frame.
  useEffect(() => {
    let rafId: number;
    const start = performance.now();
    const duration = isDragging ? 0 : 200;

    const tick = (now: number) => {
      updatePinPosition();
      if (now - start < duration) {
        rafId = requestAnimationFrame(tick);
      }
    };
    rafId = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(rafId);
  }, [transform, isDragging, updatePinPosition]);

  const handleWheel = useCallback(
    (e: WheelEvent) => {
      e.preventDefault();
      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const point = { x: e.clientX - rect.left, y: e.clientY - rect.top };
      const delta = e.deltaY > 0 ? -0.15 : 0.15;
      setTransform((prev) => {
        const nextScale = prev.scale + delta * prev.scale;
        const scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, nextScale));
        const contentX = (point.x - prev.x) / prev.scale;
        const contentY = (point.y - prev.y) / prev.scale;
        return clampTransform({
          scale,
          x: point.x - contentX * scale,
          y: point.y - contentY * scale,
        });
      });
    },
    [clampTransform]
  );

  // Attached manually with { passive: false } so preventDefault() works —
  // React's onWheel prop is passive and can't block page scroll on wheel-zoom.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    (e.target as Element).setPointerCapture?.(e.pointerId);
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pointersRef.current.size === 1) {
      panStartRef.current = { x: e.clientX, y: e.clientY };
      setIsDragging(true);
    } else if (pointersRef.current.size === 2) {
      const pts = Array.from(pointersRef.current.values());
      const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      pinchStartRef.current = { dist, scale: 1 };
      panStartRef.current = null;
    }
  }, []);

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!pointersRef.current.has(e.pointerId)) return;
      pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();

      if (pointersRef.current.size === 2) {
        // Pinch to zoom, anchored on the midpoint between the two touches.
        const pts = Array.from(pointersRef.current.values());
        const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
        const mid = {
          x: (pts[0].x + pts[1].x) / 2 - rect.left,
          y: (pts[0].y + pts[1].y) / 2 - rect.top,
        };
        if (pinchStartRef.current) {
          const ratio = dist / pinchStartRef.current.dist;
          zoomAt(mid, transform.scale * ratio);
          pinchStartRef.current = { dist, scale: 1 };
        }
        return;
      }

      if (panStartRef.current) {
        const dx = e.clientX - panStartRef.current.x;
        const dy = e.clientY - panStartRef.current.y;
        panStartRef.current = { x: e.clientX, y: e.clientY };
        setTransform((prev) => clampTransform({ ...prev, x: prev.x + dx, y: prev.y + dy }));
      }
    },
    [clampTransform, zoomAt, transform.scale]
  );

  const endPointer = useCallback((e: React.PointerEvent) => {
    pointersRef.current.delete(e.pointerId);
    if (pointersRef.current.size === 0) {
      panStartRef.current = null;
      setIsDragging(false);
    }
    if (pointersRef.current.size < 2) {
      pinchStartRef.current = null;
    }
  }, []);

  // Draws the active route's path with a stroke-dashoffset "line draw"
  // animation and instantly hides every other route.
  useEffect(() => {
    const group = routesGroupRef.current;
    if (!group) return;

    Array.from(group.children).forEach((child) => {
      const path = child as SVGPathElement;
      const length = path.getTotalLength();

      if (path.dataset.routeId === activeRouteId) {
        path.style.transition = 'none';
        path.style.strokeDasharray = `${length}`;
        path.style.strokeDashoffset = `${length}`;
        path.style.opacity = '1';
        // Force a reflow so the browser registers the reset above before the
        // transition below is applied, otherwise it animates from 0 (no-op).
        path.getBoundingClientRect();
        requestAnimationFrame(() => {
          path.style.transition = 'stroke-dashoffset 1.1s cubic-bezier(0.65, 0, 0.35, 1)';
          path.style.strokeDashoffset = '0';
        });
      } else {
        path.style.transition = 'none';
        path.style.strokeDasharray = `${length}`;
        path.style.strokeDashoffset = `${length}`;
        path.style.opacity = '0';
      }
    });
  }, [activeRouteId, svgMarkup]);

  const toggleRoute = (id: string) => {
    setActiveRouteId((current) => (current === id ? null : id));
  };

  const zoomIn = () => {
    const container = containerRef.current;
    if (!container) return;
    const { width, height } = container.getBoundingClientRect();
    zoomAt({ x: width / 2, y: height / 2 }, transform.scale + 0.5);
  };

  const zoomOut = () => {
    const container = containerRef.current;
    if (!container) return;
    const { width, height } = container.getBoundingClientRect();
    zoomAt({ x: width / 2, y: height / 2 }, transform.scale - 0.5);
  };

  const resetView = () => setTransform({ scale: 1, x: 0, y: 0 });

  useEffect(() => {
    const container = stageRef.current;
    if (!container || !svgMarkup) return;

    const svg = container.querySelector('svg');
    if (!svg) return;

    svg.removeAttribute('width');
    svg.removeAttribute('height');
    svg.setAttribute('width', '100%');
    svg.setAttribute('height', '100%');
    svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    svg.style.width = '100%';
    svg.style.height = '100%';

    // Scale the whole artwork up so it fills the wrapper more generously.
    // Applied before the crop below, so the crop frames the scaled content.
    const rootGroup = svg.querySelector<SVGGElement>('g.st40');
    if (rootGroup) {
      rootGroup.setAttribute('transform', 'scale(1.25)');
    }

    // The artwork only occupies part of the original canvas — crop the
    // viewBox to the actual drawing so it fills the container instead of
    // floating in a sea of whitespace.
    try {
      const bbox = svg.getBBox();
      const padding = Math.max(bbox.width, bbox.height) * 0.03;
      svg.setAttribute(
        'viewBox',
        `${bbox.x - padding} ${bbox.y - padding} ${bbox.width + padding * 2} ${bbox.height + padding * 2}`
      );
    } catch {
      // getBBox can throw if the svg isn't rendered yet; ignore and keep original viewBox.
    }

    const stores = Array.from(
      svg.querySelectorAll<SVGGraphicsElement>('.mapplic-clickable.stores')
    );

    const cleanups: Array<() => void> = [];

    stores.forEach((el) => {
      const id = el.id;
      el.style.cursor = 'pointer';
      el.style.transition = 'opacity 0.2s ease';

      const name = id === DKSN_SHOP_ID ? 'DKSN Business Solutions' : STORE_NAMES[id];

      const onEnter = () => {
        setHoveredId(id);
        el.style.opacity = '0.7';
        onHoverStore?.(name ?? null);
      };
      const onLeave = () => {
        setHoveredId((current) => (current === id ? null : current));
        el.style.opacity = '';
        onHoverStore?.(null);
      };
      const onClick = () => {
        if (identifyMode) {
          // eslint-disable-next-line no-console
          console.log('Clicked shape id:', id);
        }
      };

      el.addEventListener('mouseenter', onEnter);
      el.addEventListener('mouseleave', onLeave);
      el.addEventListener('click', onClick);

      cleanups.push(() => {
        el.removeEventListener('mouseenter', onEnter);
        el.removeEventListener('mouseleave', onLeave);
        el.removeEventListener('click', onClick);
      });
    });

    // Directions overlay: one hidden path per entrance, drawn in on demand.
    // Appended to the same group as the store shapes (g.st40) so it inherits
    // that group's scale(1.25) and stays glued to the artwork under pan/zoom.
    let routesGroup = svg.getElementById('dksn-routes') as SVGGElement | null;
    if (!routesGroup && rootGroup) {
      routesGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      routesGroup.setAttribute('id', 'dksn-routes');
      ENTRANCE_ROUTES.forEach((route) => {
        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        path.setAttribute('d', toSmoothPath(route.waypoints));
        path.setAttribute('fill', 'none');
        path.setAttribute('stroke', BRAND_BLUE);
        path.setAttribute('stroke-width', '7');
        path.setAttribute('stroke-linecap', 'round');
        path.setAttribute('stroke-linejoin', 'round');
        path.dataset.routeId = route.id;
        path.style.opacity = '0';
        path.style.filter = 'drop-shadow(0 0 5px rgba(91, 141, 239, 0.85))';
        routesGroup?.appendChild(path);
      });
      rootGroup.appendChild(routesGroup);
    }
    routesGroupRef.current = routesGroup;

    // DKSN's shop is always pinned and highlighted.
    const target = DKSN_SHOP_ID
      ? (svg.getElementById(DKSN_SHOP_ID) as SVGGraphicsElement | null)
      : null;

    if (target) {
      targetElRef.current = target;
      target.style.fill = BRAND_BLUE;
      target.style.stroke = '#ffffff';
      target.style.strokeWidth = '1.5';

      // Wait a frame so the browser has laid out/painted the SVG before we
      // measure it — getBoundingClientRect right after mutation can be stale.
      requestAnimationFrame(updatePinPosition);

      const resizeObserver = new ResizeObserver(updatePinPosition);
      resizeObserver.observe(containerRef.current ?? container);
      cleanups.push(() => resizeObserver.disconnect());
    }

    return () => cleanups.forEach((fn) => fn());
  }, [svgMarkup, identifyMode, updatePinPosition, onHoverStore]);

  return (
    <div
      ref={containerRef}
      className="relative h-full w-full touch-none select-none overflow-hidden [&_svg]:block"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={endPointer}
      onPointerCancel={endPointer}
      onPointerLeave={endPointer}
      style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
    >
      <div
        ref={stageRef}
        className="h-full w-full"
        style={{
          transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
          transformOrigin: '0 0',
          transition: isDragging ? 'none' : 'transform 0.15s ease-out',
        }}
        dangerouslySetInnerHTML={svgMarkup ? { __html: svgMarkup } : undefined}
      />

      {identifyMode && hoveredId && (
        <div className="pointer-events-none absolute bottom-4 left-1/2 -translate-x-1/2 rounded-lg bg-primary-950/90 px-4 py-2 text-sm font-medium text-white shadow-lg">
          Shape id: <span className="text-azure-300">{hoveredId}</span>
        </div>
      )}

      {pin && (
        <div
          className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-full"
          style={{ left: `${pin.x}px`, top: `${pin.y}px` }}
        >
          <div className="flex flex-col items-center">
            <div className="relative mb-1 flex w-max max-w-[240px] flex-col gap-1.5 rounded-lg bg-white p-2 text-[10px] shadow-lg sm:mb-1.5 sm:max-w-none sm:gap-2 sm:p-2.5 sm:text-xs">
              <div className="flex items-center gap-1.5 sm:gap-2 sm:whitespace-nowrap">
                <img
                  src="/images/logo-small.webp"
                  alt="DKSN"
                  className="h-5 w-5 shrink-0 object-contain sm:h-7 sm:w-7"
                />
                <div className="flex flex-col">
                  <span className="font-semibold leading-tight text-primary-900">
                    DKSN Business Solutions
                  </span>
                  <span className="leading-tight text-primary-600">{DKSN_ADDRESS}</span>
                </div>
              </div>
              <a
                href={DKSN_DIRECTIONS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="pointer-events-auto flex items-center justify-center gap-1.5 rounded-md bg-azure-500 px-2 py-1 text-[10px] font-semibold text-white transition-colors hover:bg-azure-600 sm:text-xs"
              >
                <Navigation className="h-3 w-3" />
                Get directions
              </a>
              <span className="absolute left-1/2 top-full -translate-x-1/2 border-4 border-transparent border-t-white" />
            </div>
            <MapPinIcon className="h-6 w-6 sm:h-8 sm:w-8" />
            <div className="relative -mt-[7px] h-2 w-2 sm:-mt-[10px] sm:h-2.5 sm:w-2.5">
              <span className="absolute inset-0 animate-ping rounded-full bg-azure-400 opacity-75" />
              <span className="absolute inset-0 rounded-full bg-azure-500" />
            </div>
          </div>
        </div>
      )}

      <div className="pointer-events-auto absolute left-3 top-3 z-20 flex flex-col gap-1.5 rounded-xl border border-white/15 bg-primary-950/85 p-2.5 shadow-lg backdrop-blur-sm">
        <p className="px-0.5 text-[11px] font-semibold uppercase tracking-[0.1em] text-gray-400">
          Directions from
        </p>
        {ENTRANCE_ROUTES.map((route) => {
          const active = activeRouteId === route.id;
          return (
            <button
              key={route.id}
              type="button"
              onClick={() => toggleRoute(route.id)}
              aria-pressed={active}
              className={`flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-left text-[12.5px] font-medium transition-colors ${
                active
                  ? 'border-azure-400/60 bg-azure-500/15 text-white'
                  : 'border-white/10 text-gray-300 hover:border-white/20 hover:bg-white/5'
              }`}
            >
              <span
                className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${
                  active ? 'bg-azure-400 text-primary-950' : 'bg-white/10 text-gray-300'
                }`}
              >
                {route.label}
              </span>
              {route.name}
            </button>
          );
        })}
      </div>

      <div className="pointer-events-auto absolute bottom-3 right-3 z-20 flex flex-col overflow-hidden rounded-xl border border-white/15 bg-primary-950/85 shadow-lg backdrop-blur-sm">
        <button
          type="button"
          aria-label="Zoom in"
          onClick={zoomIn}
          className="flex h-9 w-9 items-center justify-center text-white transition-colors hover:bg-white/10"
        >
          <Plus className="h-4 w-4" />
        </button>
        <div className="h-px w-full bg-white/10" />
        <button
          type="button"
          aria-label="Zoom out"
          onClick={zoomOut}
          className="flex h-9 w-9 items-center justify-center text-white transition-colors hover:bg-white/10"
        >
          <Minus className="h-4 w-4" />
        </button>
        <div className="h-px w-full bg-white/10" />
        <button
          type="button"
          aria-label="Reset map view"
          onClick={resetView}
          className="flex h-9 w-9 items-center justify-center text-white transition-colors hover:bg-white/10"
        >
          <RotateCcw className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

const MapPinIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" className={`drop-shadow-lg ${className ?? 'h-8 w-8'}`}>
    <path
      d="M12 2C7.58 2 4 5.58 4 10c0 5.25 6.5 11.11 7.02 11.58a1.5 1.5 0 0 0 1.96 0C13.5 21.11 20 15.25 20 10c0-4.42-3.58-8-8-8z"
      fill="#5B8DEF"
      stroke="#ffffff"
      strokeWidth="1.5"
    />
    <circle cx="12" cy="10" r="3" fill="#ffffff" />
  </svg>
);

export default MallMap;
